/**
 * The activity card's own description line: what the search result card shows
 * under the facts. `activity.shortDescription` is partner prose written for the
 * detail page — on a card it runs past the clamp and ends mid-sentence.
 *
 * Lives in `@funkey/constants` (a leaf package both `apps/web` and `apps/api`
 * already depend on) because three places need the same numbers and model list:
 * the backfill command in `apps/api`, the admin route, and the admin dropdown,
 * which is a client component and so cannot reach server code.
 */

/** Hard ceiling, and what the prompt asks for.
 *
 * Measured against the 365px card: past about ninety characters the second line
 * runs out and `line-clamp-2` cuts the sentence off on the page, whatever the
 * database holds. French says the same thing in more room than Dutch, so this
 * is the point where all three languages still fit rather than where Dutch
 * alone does. */
export const CARD_DESCRIPTION_MAX_CHARS = 90;

/** What the prompt aims for. The ceiling above is what is enforced, and the two
 * have to agree: ask for more words than the ceiling holds and the model writes
 * a sentence that is then cut off mid-clause. Twelve Dutch words land under
 * ninety characters with room to spare. */
export const CARD_DESCRIPTION_TARGET_WORDS = 12;

export type CardDescriptionModel = {
    /** Vercel AI Gateway model id, passed straight to `gateway(id)`. */
    readonly id: string;
    readonly label: string;
    readonly description: string;
};

/** Models the backfill may run on. The first entry is the default. */
export const CARD_DESCRIPTION_MODELS = [
    {
        id: 'anthropic/claude-sonnet-4.6',
        label: 'Claude Sonnet 4.6',
        description: 'Default — the copy reads closest to how we write.',
    },
    {
        id: 'anthropic/claude-sonnet-5',
        label: 'Claude Sonnet 5',
        description: 'Anthropic — newer than 4.6 and cheaper per token.',
    },
    {
        id: 'anthropic/claude-opus-5',
        label: 'Claude Opus 5',
        description: 'Anthropic — the step up from Sonnet, slower and dearer.',
    },
    {
        id: 'deepseek/deepseek-v4.1-flash',
        label: 'DeepSeek v4.1 Flash',
        description: 'Fast and cheap, enough for one sentence per language.',
    },
    {
        id: 'deepseek/deepseek-v4-flash',
        label: 'DeepSeek v4 Flash',
        description: 'Previous DeepSeek Flash generation.',
    },
    {
        id: 'google/gemini-3.8-flash',
        label: 'Gemini 3.8 Flash',
        description: 'Google — strong Dutch and French phrasing.',
    },
    {
        id: 'openai/gpt-4o-mini',
        label: 'GPT-4o mini',
        description: 'OpenAI — the model the translation backfill uses.',
    },
] as const satisfies ReadonlyArray<CardDescriptionModel>;

export type CardDescriptionModelId = (typeof CARD_DESCRIPTION_MODELS)[number]['id'];

export const DEFAULT_CARD_DESCRIPTION_MODEL_ID: CardDescriptionModelId =
    CARD_DESCRIPTION_MODELS[0].id;

export const CARD_DESCRIPTION_MODEL_IDS: ReadonlyArray<CardDescriptionModelId> =
    CARD_DESCRIPTION_MODELS.map(model => model.id);

export function isValidCardDescriptionModelId(value: unknown): value is CardDescriptionModelId {
    return CARD_DESCRIPTION_MODEL_IDS.some(id => id === value);
}

export type CardDescriptionPromptStyle = {
    readonly key: string;
    readonly label: string;
    /** What this angle sells, in one line, for the comparison report. */
    readonly description: string;
    /** The sentence of this style the model is asked to match. */
    readonly example: string;
    /** The rules that replace the neutral ones. Everything else — the length,
     * the ban on invented facts, the languages — holds for every style. */
    readonly rules: readonly string[];
};

/**
 * Angles on the same activity. The card sentence can say what the group does,
 * what the group gets out of it, or what the room feels like. Comparing the
 * three on real activities put the mix ahead: the action alone reads flat on a
 * card, and the other two only land when the activity happens to offer that
 * half. The pure three stay here so the comparison can be re-run.
 *
 * The first entry is what the backfill uses.
 */
export const CARD_DESCRIPTION_PROMPT_STYLES = [
    {
        key: 'mix',
        label: 'Gemengd',
        description:
            'The action first, closed with whatever the activity itself offers — what it asks of the team, or what the room is like. What the backfill uses.',
        example:
            "Bereid in groepjes verschillende Aziatische hapjes zoals dimsums en loempia's en proef ze samen.",
        rules: [
            '- Open with what the group actually DOES, concretely. "Gooi bijlen naar houten doelen" beats "Een unieke ervaring".',
            '- Name the real things they handle, taste, build or throw. A concrete noun is worth three adjectives.',
            '- Close the sentence with whatever that activity gives you: what it asks of the team, or what the room looks and sounds like while it runs. Take the one the activity data actually supports, and leave it out rather than reach.',
            '- That closing half is one short clause. Never a promise about "teamspirit" or "verbinding", and never invented weather, venue, music or crowd.',
        ],
    },
    {
        key: 'doen',
        label: 'Wat je doet',
        description: 'The action alone, flatly. No selling — the facts do it.',
        example:
            "Bereid in groepjes verschillende Aziatische hapjes zoals dimsums en loempia's en proef ze samen.",
        rules: [
            '- Say what the group actually DOES, concretely. "Gooi bijlen naar houten doelen" beats "Een unieke ervaring".',
            '- Name the real things they handle, taste, build or throw. A concrete noun is worth three adjectives.',
            '- State it flatly. The activity sells itself; the sentence only has to say what it is.',
        ],
    },
    {
        key: 'samen',
        label: 'Wat het oplevert',
        description:
            'The action plus what the team gets out of it — one short clause, no promises.',
        example:
            'Los samen puzzels op onder tijdsdruk en ontdek wie in je team het hoofd koel houdt.',
        rules: [
            '- Say what the group does, then close with what the team gets out of it: who has to talk to whom, what it asks of them.',
            '- That second half is one short clause, drawn from the activity itself. Never a promise about "teamspirit" or "verbinding".',
            '- Name the real things they handle, taste, build or throw. A concrete noun is worth three adjectives.',
        ],
    },
    {
        key: 'sfeer',
        label: 'Hoe het voelt',
        description: 'The scene — what it sounds and looks like while it happens.',
        example:
            'Ren door een donker parcours vol lichtsignalen terwijl je teamgenoten je positie doorschreeuwen.',
        rules: [
            '- Put the reader in the room: what they see, hear or handle while the activity runs.',
            '- Use verbs to do it, not adjectives. Describe the scene, never how much fun it is.',
            '- The scene comes out of the activity data: no invented weather, venue, music or crowd.',
        ],
    },
] as const satisfies ReadonlyArray<CardDescriptionPromptStyle>;

export type CardDescriptionPromptStyleKey = (typeof CARD_DESCRIPTION_PROMPT_STYLES)[number]['key'];

export const DEFAULT_CARD_DESCRIPTION_PROMPT_STYLE: CardDescriptionPromptStyleKey =
    CARD_DESCRIPTION_PROMPT_STYLES[0].key;

export const CARD_DESCRIPTION_PROMPT_STYLE_KEYS: ReadonlyArray<CardDescriptionPromptStyleKey> =
    CARD_DESCRIPTION_PROMPT_STYLES.map(style => style.key);

const LANGUAGE_LABELS: Record<string, string> = {
    nl: 'Dutch',
    fr: 'French',
    en: 'English',
};

/**
 * The one instruction set the backfill and the admin "generate" button share.
 * Kept blunt about length: a model told "short" writes two sentences.
 *
 * `languages` comes from the caller (`I18N_CONF.LANGUAGES`) rather than an
 * import — this package has no dependencies and is kept that way.
 */
export function buildCardDescriptionSystemPrompt(
    languages: readonly string[],
    style: string = DEFAULT_CARD_DESCRIPTION_PROMPT_STYLE,
): string {
    const angle =
        CARD_DESCRIPTION_PROMPT_STYLES.find(entry => entry.key === style) ??
        CARD_DESCRIPTION_PROMPT_STYLES[0];

    return [
        'You write the one-line description shown on a teambuilding activity card in a search result list, for the Belgian activity platform Funkey.',
        '',
        'HARD RULES',
        `- At most ${CARD_DESCRIPTION_MAX_CHARS} characters per language, including spaces. This is a hard limit.`,
        `- Aim for about ${CARD_DESCRIPTION_TARGET_WORDS} words: one complete sentence that fits on two short lines.`,
        `- This is the length and the tone to match: "${angle.example}"`,
        ...angle.rules,
        '- Only facts from the activity data given to you. Never invent a location, a price, a duration, or an item.',
        '- No marketing filler: drop "uniek", "onvergetelijk", "ultieme", "perfecte", "boordevol plezier".',
        '- No activity name, no partner name, no price, no participant count — the card already shows those.',
        '- No quotes, no emoji, no markdown, no trailing ellipsis. End with a full stop.',
        '- Address the group informally where the language has the choice (Dutch "je", French "vous").',
        '',
        'LANGUAGES',
        `- Write the sentence for every language asked for (${languages
            .map(l => `${l} = ${LANGUAGE_LABELS[l] ?? l}`)
            .join(', ')}).`,
        '- Each one is written natively, not translated word for word, and each obeys the character limit on its own.',
    ].join('\n');
}

/**
 * Last line of defence on the character limit: models overshoot, and a card that
 * clamps mid-sentence is the thing this whole feature exists to stop. Cuts back
 * to the last sentence that fits, or failing that the last whole word.
 */
export function clampCardDescription(text: string): string {
    const cleaned = text.replace(/\s+/g, ' ').trim();
    if (cleaned.length <= CARD_DESCRIPTION_MAX_CHARS) return cleaned;

    const head = cleaned.slice(0, CARD_DESCRIPTION_MAX_CHARS);

    const lastSentenceEnd = Math.max(head.lastIndexOf('. '), head.lastIndexOf('! '));
    if (lastSentenceEnd > 0) return head.slice(0, lastSentenceEnd + 1);

    // No space to break on: the model returned one long token. Cut a character
    // short so the full stop still fits inside the limit.
    const lastSpace = head.lastIndexOf(' ');
    const trimmed = (
        lastSpace > 0 ? head.slice(0, lastSpace) : head.slice(0, CARD_DESCRIPTION_MAX_CHARS - 1)
    ).replace(/[\s,;:—-]+$/, '');
    return trimmed.endsWith('.') ? trimmed : `${trimmed}.`;
}
